import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseFormComponent } from '@classes/extends';
import { FormValidator } from '@composants/validators';
import { FicheclientService as FicheClientService, GroupeTech, Groupes, RFGroupe } from '@estel-numerique/galaclick-api';
import { DateHelper, TraductionCoreService, ValidatorPatternService } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { lastValueFrom } from 'rxjs';
import { DialogGalaComponent } from '../../../../composants/dialog-gala/dialog-gala.component';
import { SelectGala } from '../../../../composants/select/select.component';
import { DateGalaComponent } from '../../../../composants/date-gala/date-gala.component';
import { MatButton } from '@angular/material/button';

export interface FicheClientTechniquesComponentData {
    idClient: number;
    listeGroupesTech: Groupes;
    groupe?: GroupeTech;
}
@Component({
    templateUrl: 'ficheclient-techniques.component.html',
    imports: [DialogGalaComponent, FormsModule, ReactiveFormsModule, SelectGala, DateGalaComponent, MatButton]
})
export class FicheClientTechniquesComponent extends BaseFormComponent implements OnInit {
    public traductionCore = inject(TraductionCoreService);
    public validPattern = inject(ValidatorPatternService);
    private formBuilder = inject(UntypedFormBuilder);
    private ref = inject(ChangeDetectorRef);
    private wsFicheClient = inject(FicheClientService);
    private dialogRef = inject<MatDialogRef<FicheClientTechniquesComponent>>(MatDialogRef);
    public data = inject<FicheClientTechniquesComponentData>(MAT_DIALOG_DATA);

    public codeModule: string = Constantes.CodeOption.CFICHECLIENT; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)

    @Input() public titre: string;
    @Output() saved = new EventEmitter<GroupeTech>();

    // Traductions
    protected lgTrad: Record<string, string> = {};

    public groupe: GroupeTech = {};
    public listeGroupes: RFGroupe[];
    public idClient: number;
    public mulcln: number;
    public isSuppression: boolean;
    public clientHasGroupes: boolean;

    constructor() {
        super();
        this.lgTrad = {
            AFFGRP: this.translate.instant('APPFICHECLIENT.AFFGRP'),
            GROUPES: this.translate.instant('APPFICHECLIENT.GROUPES'),
            DEBUT: this.translate.instant('DEBUT'),
            FIN: this.translate.instant('FIN'),
            BEGINNINGDATE: this.translate.instant('BEGINNINGDATE'),
            ENDDATE: this.translate.instant('ENDDATE'),
            ENR: this.translate.instant('ENR'),
            RET: this.translate.instant('RET'),
        };
        this.formData = this.formBuilder.group({
            RFGROUPE: [null, Validators.required],
            DDEBUT: [null, [Validators.required, FormValidator.validDate]],
            DFIN: [null, [FormValidator.validDate]],
        }, { validators: [this.util.date.compareDate('DDEBUT', 'DFIN')] });
    }
    ngOnInit() {
        super.ngOnInit();
        this.show(this.data.idClient, this.data.listeGroupesTech, this.data.groupe).catch(e => this.util.fErreur(e));
    }

    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    private async show(idClient: number, groupesTech: Groupes, groupe?: GroupeTech) {
        try {
            this.util.showLoader();

            this.idClient = idClient;
            this.mulcln = groupesTech.MULCLN;
            const listGrpClient: GroupeTech[] = groupesTech.GROUPESTECH;
            this.clientHasGroupes = (groupesTech.GROUPESTECH.length >= 1) ? true : false;
            const listeTmp: RFGroupe[] = await this.wsCacheLow.getRefGroupe().toPromise();

            if (groupe) {
                this.groupe = groupe;
                this.listeGroupes = listeTmp;
                this.isSuppression = true;
            } else {
                this.listeGroupes = listeTmp.filter((rf: RFGroupe) => {
                    let boOk = true;
                    listGrpClient.forEach((grp: GroupeTech) => {
                        if (rf.IDRFGROUPE === grp.RFGROUPE.IDRFGROUPE) {
                            boOk = false;
                        }
                    });
                    return boOk;
                });
                this.isSuppression = false;
            }

            this.formData.markAsPristine();
            this.formData.markAsUntouched();

            this.chargerForm();

        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    async enregistrerGroupe() {
        this.util.showLoader();
        try {
            this.validationFormulaire(this.formData);
            this.getResultatFromForm();
            if (this.formData.valid) {
                if (this.isSuppression) {
                    this.groupe = await lastValueFrom(this.wsFicheClient.putGroupe(this.idClient, this.groupe));
                } else {
                    this.groupe = await lastValueFrom(this.wsFicheClient.postGroupe(this.idClient, this.mulcln, this.groupe));
                }
                this.saved.emit(this.groupe);
                this.hideModal();
            }
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    public getResultatFromForm() {
        Object.assign(this.groupe, this.formData.value);
        if (!this.isSuppression) {
            this.groupe.FAVORI = (this.clientHasGroupes) ? 0 : 1;
        }
    }

    public chargerForm() {
        if (this.isSuppression) {
            this.ref.detectChanges();

            this.groupe.RFGROUPE = this.listeGroupes.find((q: RFGroupe) => {
                return this.groupe.RFGROUPE.IDRFGROUPE == q.IDRFGROUPE;
            });
            if (!this.groupe.DFIN) {
                this.groupe.DFIN = DateHelper.getDateJourISO();
            }

            this.formData.reset(this.groupe);
            this.formData.markAsUntouched();

            this.formData.get('DDEBUT').setValue(null);
            this.formData.get('DDEBUT').clearValidators();
            this.formData.get('DDEBUT').updateValueAndValidity();
            this.formData.get('DFIN').setValidators([Validators.required]);
        } else {
            this.formData.get('DDEBUT').setValidators([Validators.required]);
            this.formData.get('DFIN').setValue(null);
            this.formData.get('DFIN').clearValidators();
            this.formData.get('DFIN').updateValueAndValidity();
        }
    }

    askHideModal() {
        if (!this.isSuppression && !this.formData.untouched) {
            this.util.fConfirm(this.translate.instant('QUITNOSAVE'), () => {
                this.hideModal();
            }, this.translate.instant('MODIFDONE'));
        } else {
            this.hideModal();
        }
    }

    hideModal() {
        this.resetModalValue();
        this.dialogRef.close();
    }

    resetModalValue() {
        this.groupe = {};
        this.formData.reset();
        this.formData.markAsPristine();
        this.formData.markAsUntouched();
    }

}
